<template>
  <span
    :class="['ui-input', `ui-input--${size}`, { 'is-invalid': invalid, 'is-disabled': disabled }]"
  >
    <span v-if="$slots['leading']" class="ui-input__adorn" aria-hidden="true"
      ><slot name="leading"
    /></span>
    <input
      v-model="model"
      class="ui-input__field"
      :type="type"
      :disabled="disabled"
      :aria-invalid="invalid || undefined"
      v-bind="$attrs"
    />
    <span v-if="$slots['trailing']" class="ui-input__adorn"><slot name="trailing" /></span>
  </span>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false })

withDefaults(
  defineProps<{
    type?: 'text' | 'search' | 'email' | 'number' | 'password' | 'url' | 'tel' | 'date'
    size?: 'sm' | 'md'
    invalid?: boolean
    disabled?: boolean
  }>(),
  { type: 'text', size: 'md', invalid: false, disabled: false },
)

const model = defineModel<string | number | undefined>({ default: undefined })
</script>

<style scoped>
.ui-input {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text);
  transition:
    border-color var(--transition-base) ease,
    box-shadow var(--transition-base) ease;
}

.ui-input--sm {
  padding: 0 var(--space-2);
  min-height: 28px;
}

.ui-input--md {
  padding: 0 var(--space-3);
  min-height: 34px;
}

.ui-input:hover:not(.is-disabled) {
  border-color: var(--color-border-strong);
}

.ui-input:focus-within {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-focus);
}

.ui-input.is-invalid {
  border-color: var(--color-danger);
}

.ui-input.is-disabled {
  background: var(--color-surface-sunken);
  opacity: 0.7;
}

.ui-input__field {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: var(--text-sm);
  color: inherit;
}

.ui-input__field:focus,
.ui-input__field:focus-visible {
  outline: none;
  box-shadow: none;
}

.ui-input__field::placeholder {
  color: var(--color-text-subtle);
}

.ui-input__adorn {
  display: inline-flex;
  flex: none;
  color: var(--color-text-muted);
}
</style>
